import { Map } from 'immutable';

import type { StakeFromBackend } from 'app/api/services/active-stakes/types';

import { createImmutableMatchPeriods, createImmutableMatchScores } from 'app/lib/betting/betting-entities/reducers/matches/utils/immutable-structures';
import { createEntityMapHandler } from 'app/lib/redux/applyEntities';
import { getBetOutcomeStatus, getStakeOutcomeStatus } from 'app/lib/staking/utils';
import { TRANSFORM_SSR_STATE } from 'app/redux/ssrState';

import { CLEANUP_ACTIVE_STAKES, CleanupActiveStakes } from '../actions/activeStakes';
import {
  CLEANUP_HISTORY_STAKES,
  CleanupHistoryStakes,
  fetchHistoryStakes,
} from '../actions/historyStakes';
import { RecordOfStake, StakeBetEntity, StakeEntity } from '../records';
import type { EntriesStakes, EntriesState } from '../types/entries';

// ---

export const initialState: EntriesState = {
  entries: Map(),
  loading: false,
  requested: false,
  error: null,
};

export const applyEntries = createEntityMapHandler<
  EntriesStakes,
  StakeFromBackend,
  typeof createStakeEntry
>('id', createStakeEntry);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TransformSSRState = any;

type EntriesActions =
  | TransformSSRState
  | CleanupActiveStakes
  | CleanupHistoryStakes;

type REQUEST =
  | typeof fetchHistoryStakes.pending.type;
type SUCCESS =
  | typeof fetchHistoryStakes.fulfilled.type;
type FAILURE =
  | typeof fetchHistoryStakes.rejected.type;
type CLEANUP =
  | typeof CLEANUP_ACTIVE_STAKES
  | typeof CLEANUP_HISTORY_STAKES;

type ActionTypes = {
  REQUEST: REQUEST;
  SUCCESS: SUCCESS;
  FAILURE: FAILURE;
  CLEANUP: CLEANUP;
};

export function createStakeListReducer({ REQUEST, SUCCESS, FAILURE, CLEANUP }: ActionTypes) {
  return <T extends EntriesState>(state = (initialState as T), action: EntriesActions): T => {
    switch (action.type) {
      case TRANSFORM_SSR_STATE:
        return {
          ...state,
          entries: Map(state.entries),
        };

      case REQUEST:
        return {
          ...state,
          loading: true,
          requested: false,
        };

      case SUCCESS:
        return {
          ...state,
          loading: false,
          requested: true,
          entries: applyEntries(state.entries, action.payload.data),
        };

      case FAILURE:
        return {
          ...state,
          loading: false,
          requested: true,
          error: action.error,
        };

      case CLEANUP:
        return initialState as T;

      default:
        return state;
    }
  };
}

// ---

export function createStakeEntry(payload: StakeFromBackend): RecordOfStake {
  const bets = payload.bets.map((bet) => (
    StakeBetEntity({
      ...bet,
      status: getBetOutcomeStatus(bet.state),
      periods: bet.periods && createImmutableMatchPeriods(bet.periods),
      scores: bet.scores && createImmutableMatchScores(bet.scores),
      matchStartDate:
        bet.matchStartDate ? new Date(bet.matchStartDate) : null,
    })
  ));

  return StakeEntity({
    ...payload,
    createdAt:
      payload.createdAt ? new Date(payload.createdAt) : null,
    settledAt:
      payload.settledAt ? new Date(payload.settledAt) : null,
    status: getStakeOutcomeStatus(payload.state),
    bets,
  });
}
